import { isMap, isScalar, isSeq, parseDocument, Scalar, YAMLMap, YAMLSeq, type Document, type Pair } from "yaml";

/**
 * An `idf_component.yml` read from its text: the component's metadata and its
 * dependencies, and edits to them. An edit changes the YAML document's nodes
 * and prints it again, so comments, quoting and keys outside the edit stay as
 * they were.
 */

export const DEFAULT_NAMESPACE = "espressif";

export type Source = "registry" | "git" | "path";
export type Require = "private" | "public" | "no";

export interface Condition {
    if: string;
    version: string;
}

export interface Dependency {
    /** The key under `dependencies`, as written. */
    name: string;
    source: Source;
    version: string;
    git: string;
    path: string;
    overridePath: string;
    registryUrl: string;
    /** As written; "" when the entry has no `require`, which means private. */
    require: Require | "";
    preRelease: boolean;
    /** Conditions that must all hold, and conditions of which one must hold; the entry is used when both are met. */
    rules: Condition[];
    matches: Condition[];
}

export const METADATA_TEXT = [
    "version",
    "description",
    "license",
    "url",
    "repository",
    "documentation",
    "issues",
    "discussion",
] as const;
export const METADATA_LISTS = ["targets", "tags", "maintainers"] as const;
export type MetadataText = (typeof METADATA_TEXT)[number];
export type MetadataList = (typeof METADATA_LISTS)[number];
export type Metadata = Record<MetadataText, string> & Record<MetadataList, string[]>;

export interface Manifest {
    metadata: Metadata;
    /** The ESP-IDF version spec, or undefined without an `idf` entry. */
    idf: string | undefined;
    dependencies: Dependency[];
}

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

export function emptyDependency(name = ""): Dependency {
    return {
        name,
        source: "registry",
        version: "",
        git: "",
        path: "",
        overridePath: "",
        registryUrl: "",
        require: "",
        preRelease: false,
        rules: [],
        matches: [],
    };
}

/** The name the component manager gives a dependency: a registry key in lower case and a namespace. */
export function resolvedName(dep: Pick<Dependency, "name" | "source">): string {
    // A local component named `namespace__name` stands for the registry's `namespace/name`.
    if (dep.source !== "registry") return dep.name.replace("__", "/");
    const name = dep.name.toLowerCase();
    return name.includes("/") ? name : `${DEFAULT_NAMESPACE}/${name}`;
}

function text(node: unknown): string {
    if (isScalar(node) && typeof node.value === "number" && typeof node.source === "string") return node.source;
    const value = isScalar(node) ? node.value : node;
    return value === null || value === undefined ? "" : String(value);
}

function strings(node: unknown): string[] {
    return isSeq(node) ? node.items.map(text).filter((item) => item !== "") : [];
}

function load(source: string): Result<Document.Parsed> {
    const doc = parseDocument(source);
    const error = doc.errors[0];
    if (error !== undefined) {
        const line = error.linePos?.[0].line;
        return { ok: false, error: `idf_component.yml is not valid YAML${line ? ` at line ${line}` : ""}.` };
    }
    if (doc.contents !== null && !isMap(doc.contents))
        return { ok: false, error: "idf_component.yml is not a mapping." };
    const deps = doc.get("dependencies", true);
    if (deps !== undefined && !isMap(deps) && !(isScalar(deps) && deps.value === null)) {
        return { ok: false, error: "The dependencies of idf_component.yml are not a mapping." };
    }
    return { ok: true, value: doc };
}

/** The manifest in `source`, or why it cannot be read. */
export function parseManifest(source: string): Result<Manifest> {
    const loaded = load(source);
    if (!loaded.ok) return loaded;
    const doc = loaded.value;
    const metadata = {} as Metadata;
    for (const key of METADATA_TEXT) metadata[key] = text(doc.get(key, true));
    for (const key of METADATA_LISTS) metadata[key] = strings(doc.get(key, true));
    let idf: string | undefined;
    const dependencies: Dependency[] = [];
    const deps = doc.get("dependencies", true);
    if (isMap(deps)) {
        for (const pair of deps.items) {
            const name = text(pair.key);
            if (name === "idf") idf = isMap(pair.value) ? text(pair.value.get("version", true)) : text(pair.value);
            else dependencies.push(readDependency(name, pair.value));
        }
    }
    return { ok: true, value: { metadata, idf, dependencies } };
}

function readDependency(name: string, node: unknown): Dependency {
    const dep = emptyDependency(name);
    if (!isMap(node)) {
        dep.version = text(node);
        return dep;
    }
    const field = (key: string) => text(node.get(key, true));
    dep.version = field("version");
    dep.git = field("git");
    dep.path = field("path");
    dep.overridePath = field("override_path");
    dep.registryUrl = field("registry_url") || field("service_url");
    dep.source = dep.git !== "" ? "git" : dep.path !== "" ? "path" : "registry";
    const require = node.get("require");
    if (require === false || require === "no") dep.require = "no";
    else if (require === "public" || require === "private") dep.require = require;
    else if (node.get("public") === true) dep.require = "public";
    dep.preRelease = node.get("pre_release") === true;
    dep.rules = conditions(node.get("rules", true));
    dep.matches = conditions(node.get("matches", true));
    return dep;
}

function conditions(node: unknown): Condition[] {
    if (!isSeq(node)) return [];
    return node.items.filter(isMap).map((item) => ({
        if: text(item.get("if", true)),
        version: text(item.get("version", true)),
    }));
}

// ---- edits ----------------------------------------------------------------------

/** A string scalar in double quotes, as version specs and conditions are usually written. */
function quoted(doc: Document, value: string): Scalar {
    const node = doc.createNode(value) as Scalar;
    node.type = Scalar.QUOTE_DOUBLE;
    return node;
}

/** Set `key` in `map`, or remove it when `value` is undefined; a scalar keeps its node, so its comment and style stay. */
function setKey(doc: Document, map: YAMLMap, key: string, value: string | boolean | undefined, quote = true): void {
    if (value === undefined) {
        map.delete(key);
        return;
    }
    const node = map.get(key, true);
    if (isScalar(node)) node.value = value;
    else map.set(key, typeof value === "string" && quote ? quoted(doc, value) : doc.createNode(value));
}

function edit(source: string, change: (doc: Document.Parsed) => string | void): Result<string> {
    const loaded = load(source);
    if (!loaded.ok) return loaded;
    const doc = loaded.value;
    if (doc.contents === null) doc.contents = doc.createNode({}) as YAMLMap as never;
    const error = change(doc);
    if (error !== undefined) return { ok: false, error };
    const indentSeq = !UNINDENTED_SEQUENCE.test(source);
    return { ok: true, value: doc.toString({ lineWidth: 0, indentSeq, flowCollectionPadding: false }) };
}

/** A key whose block sequence starts at the key's own indentation, a style the printer keeps only when told. */
const UNINDENTED_SEQUENCE = /^( *)[^\s#-][^\n]*:[ \t]*(?:#[^\n]*)?\n(?:[ \t]*(?:#[^\n]*)?\n)*\1- /m;

function root(doc: Document): YAMLMap {
    return doc.contents as YAMLMap;
}

/** The `dependencies` mapping, created at the end of the manifest when it has none. */
function dependencyMap(doc: Document): YAMLMap {
    const node = doc.get("dependencies", true);
    if (isMap(node)) return node;
    const map = new YAMLMap();
    doc.set("dependencies", map);
    return map;
}

function findPair(map: YAMLMap, name: string): Pair | undefined {
    return map.items.find((pair) => text(pair.key) === name);
}

/**
 * Set a metadata field; an empty value removes it. A new field goes before
 * `dependencies`, where manifests usually keep their metadata.
 */
export function setMetadata(
    source: string,
    key: MetadataText | MetadataList,
    value: string | string[],
): Result<string> {
    return edit(source, (doc) => {
        const map = root(doc);
        const empty = typeof value === "string" ? value.trim() === "" : value.length === 0;
        if (empty) {
            map.delete(key);
            return;
        }
        const node = map.get(key, true);
        if (typeof value === "string" && isScalar(node)) {
            node.value = value;
            return;
        }
        if (typeof value !== "string" && isSeq(node)) {
            setItems(doc, node, value);
            return;
        }
        if (node !== undefined) {
            map.set(key, doc.createNode(value));
            return;
        }
        const at = map.items.findIndex((pair) => text(pair.key) === "dependencies");
        const pair = doc.createPair(key, doc.createNode(value));
        if (at < 0) map.items.push(pair);
        else map.items.splice(at, 0, pair);
    });
}

/** Set the ESP-IDF version spec; an empty spec removes the `idf` entry. */
export function setIdf(source: string, spec: string): Result<string> {
    return edit(source, (doc) => {
        const deps = doc.get("dependencies", true);
        if (spec.trim() === "") {
            if (isMap(deps)) deps.delete("idf");
            return;
        }
        const map = dependencyMap(doc);
        const pair = findPair(map, "idf");
        if (pair === undefined) map.items.unshift(doc.createPair("idf", quoted(doc, spec)));
        else if (isMap(pair.value)) setKey(doc, pair.value, "version", spec);
        else if (isScalar(pair.value)) pair.value.value = spec;
        else pair.value = quoted(doc, spec);
    });
}

/** Why `dep` cannot be written, or undefined. */
export function dependencyProblem(dep: Dependency, others: Dependency[]): string | undefined {
    if (dep.name.trim() === "") return "The dependency needs a name.";
    if (dep.name === "idf") return "idf is the ESP-IDF version, not a component.";
    if (dep.source === "git" && dep.git.trim() === "") return "A Git dependency needs the repository's URL.";
    if (dep.source === "path" && dep.path.trim() === "") return "A local dependency needs a path.";
    const name = resolvedName(dep);
    if (others.some((other) => resolvedName(other) === name)) return `${dep.name} is already a dependency.`;
    return undefined;
}

/**
 * Write `dep` under `dependencies`: in place of the entry `previous` when
 * given, keeping its position and comments, else at the end. An entry that
 * holds only a version is written as `name: "version"`, unless it was a mapping.
 */
export function putDependency(source: string, previous: string | undefined, dep: Dependency): Result<string> {
    return edit(source, (doc) => {
        const map = dependencyMap(doc);
        const pair = previous === undefined ? undefined : findPair(map, previous);
        if (previous !== undefined && pair === undefined) return `${previous} is no longer a dependency.`;
        if (previous !== dep.name && findPair(map, dep.name) !== undefined)
            return `${dep.name} is already a dependency.`;
        let value = pair?.value;
        if (!isMap(value) && onlyVersion(dep)) {
            const version = dep.version.trim() || "*";
            if (isScalar(value)) value.value = version;
            else value = quoted(doc, version);
        } else {
            const before = isMap(value) && pair !== undefined ? readDependency(previous!, value).source : undefined;
            if (!isMap(value)) {
                const map = new YAMLMap();
                if (isScalar(value)) map.comment = value.comment;
                value = map;
            }
            writeFields(doc, value as YAMLMap, dep, before);
        }
        if (pair === undefined) {
            map.items.push(doc.createPair(dep.name, value));
            return;
        }
        if (isScalar(pair.key)) pair.key.value = dep.name;
        else pair.key = doc.createNode(dep.name);
        pair.value = value;
    });
}

function onlyVersion(dep: Dependency): boolean {
    return (
        dep.source === "registry" &&
        dep.overridePath.trim() === "" &&
        dep.registryUrl.trim() === "" &&
        !dep.preRelease &&
        dep.require === "" &&
        dep.rules.length === 0 &&
        dep.matches.length === 0
    );
}

/** The keys the form edits for each source. */
const SOURCE_KEYS: Record<Source, string[]> = {
    registry: ["version", "override_path", "registry_url", "pre_release"],
    git: ["version", "git", "path"],
    path: ["path"],
};

/**
 * Write the form's fields of `dep` into its mapping. The keys of other
 * sources are removed when the entry's source changed from `before`; `git` and
 * `path` always are, since they decide the source. Other keys stay.
 */
function writeFields(doc: Document, map: YAMLMap, dep: Dependency, before: Source | undefined): void {
    const value = (text: string) => (text.trim() === "" ? undefined : text.trim());
    const values: Record<string, string | boolean | undefined> = {
        version: value(dep.version),
        git: value(dep.git),
        path: value(dep.path),
        override_path: value(dep.overridePath),
        registry_url: value(dep.registryUrl),
        pre_release: dep.preRelease || undefined,
    };
    const shown = SOURCE_KEYS[dep.source];
    for (const key of Object.keys(values)) {
        if (shown.includes(key)) setKey(doc, map, key, values[key]);
        else if (key === "git" || key === "path" || before !== dep.source) map.delete(key);
    }
    // The deprecated spellings of registry_url and `require: public`.
    if (values.registry_url !== undefined || dep.source !== "registry") map.delete("service_url");
    if (dep.require !== "") map.delete("public");
    setKey(doc, map, "require", dep.require === "" ? undefined : dep.require);
    writeConditions(doc, map, "rules", dep.rules);
    writeConditions(doc, map, "matches", dep.matches);
}

/** Write a list of conditions, keeping the items whose `if` is unchanged, with their comments. */
function writeConditions(doc: Document, map: YAMLMap, key: "rules" | "matches", list: Condition[]): void {
    const wanted = list.filter((condition) => condition.if.trim() !== "");
    if (wanted.length === 0) {
        map.delete(key);
        return;
    }
    const existing: unknown = map.get(key, true);
    const seq = isSeq(existing) ? existing : new YAMLSeq();
    if (seq !== existing) map.set(key, seq);
    const old = seq.items.filter(isMap);
    const items = wanted.map((condition) => {
        const at = old.findIndex((item) => text(item.get("if", true)) === condition.if.trim());
        const item = at < 0 ? new YAMLMap() : old.splice(at, 1)[0]!;
        if (at < 0) item.set("if", quoted(doc, condition.if.trim()));
        setKey(doc, item, "version", condition.version.trim() === "" ? undefined : condition.version.trim());
        return item;
    });
    setItems(doc, seq, items);
}

/** Replace the items of `seq`, reusing the scalar nodes whose values stay, with their comments. */
function setItems(doc: Document, seq: YAMLSeq, values: (string | YAMLMap)[]): void {
    const old = [...seq.items];
    seq.items = values.map((value) => {
        if (typeof value !== "string") return value;
        const at = old.findIndex((item) => isScalar(item) && text(item) === value);
        return at < 0 ? doc.createNode(value) : old.splice(at, 1)[0];
    });
}

export function removeDependency(source: string, name: string): Result<string> {
    return edit(source, (doc) => {
        const deps = doc.get("dependencies", true);
        if (!isMap(deps) || !deps.has(name)) return `${name} is not a dependency.`;
        deps.delete(name);
    });
}

/** The smallest edit that turns `before` into `after`: their common start and end are left out. */
export function textEdit(before: string, after: string): { from: number; to: number; expect: string; insert: string } {
    let start = 0;
    while (start < before.length && start < after.length && before[start] === after[start]) start++;
    let end = 0;
    while (
        end < before.length - start &&
        end < after.length - start &&
        before[before.length - 1 - end] === after[after.length - 1 - end]
    )
        end++;
    return {
        from: start,
        to: before.length - end,
        expect: before.slice(start, before.length - end),
        insert: after.slice(start, after.length - end),
    };
}
